import React, { useCallback, useEffect, useState } from 'react';
import { Fingerprint, ShieldCheck, Smartphone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  browserSupportsPasskeys,
  enrollPasskey,
  hasLocalPasskeyFlag,
  isPasskeySupportedOnThisDevice,
  listPasskeys,
  revokePasskey,
  setLocalPasskeyFlag,
} from '../utils/passkeys';

/**
 * Account-Settings card to enrol or revoke a passkey on the current
 * device. Mounted on every role's "Login & Security" page so guests,
 * hosts, venues, staff and admins all get the same control.
 *
 * UX rules:
 *  - Hidden entirely when the device can't do WebAuthn at all (older
 *    browsers, desktop without any authenticator). We never lie.
 *  - When the device CAN do WebAuthn but the user has no enrolled
 *    passkeys yet, render a friendly explainer + enrol CTA.
 *  - Once they have at least one passkey, render a list of registered
 *    devices with a per-row revoke button. Adding more passkeys (e.g.
 *    one per phone) is allowed — no artificial limit.
 */
const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";
const BRAND = '#703FF6';

const _formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const _shortDeviceLabel = (cred) => {
  if (cred.device_label) return cred.device_label;
  const ua = cred.user_agent || '';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android device';
  if (/Mac OS/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';
  return 'This device';
};

export const BiometricUnlockCard = () => {
  const [supported, setSupported] = useState(null); // null = checking, false/true once known
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const list = await listPasskeys(token);
      setCredentials(list);
      // Keep the localStorage flag in sync with reality.
      setLocalPasskeyFlag(list.length > 0 && hasLocalPasskeyFlag() ? true : list.length > 0);
    } catch {
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await isPasskeySupportedOnThisDevice();
      if (cancelled) return;
      setSupported(ok);
      if (ok) {
        await refresh();
      } else {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const handleEnrol = async () => {
    if (enrolling) return;
    setEnrolling(true);
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please sign in again before enrolling biometric unlock.');
      setEnrolling(false);
      return;
    }
    try {
      const guessLabel = (() => {
        const ua = navigator.userAgent || '';
        if (/iPhone/i.test(ua)) return 'iPhone';
        if (/iPad/i.test(ua)) return 'iPad';
        if (/Android/i.test(ua)) return 'Android phone';
        if (/Mac OS/i.test(ua)) return 'Mac';
        if (/Windows/i.test(ua)) return 'Windows PC';
        return 'This device';
      })();
      await enrollPasskey({ token, deviceLabel: guessLabel });
      toast.success('Biometric unlock enabled on this device ✓');
      await refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail
        || err?.message
        || 'Could not enable biometric unlock. Please try again.';
      // Common cancel codes from the WebAuthn spec — surface a friendlier copy.
      if (/NotAllowedError|cancel|aborted/i.test(String(err?.name || err?.message || ''))) {
        toast.error('Setup cancelled. Tap "Enable" again when you are ready.');
      } else {
        toast.error(msg);
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleRevoke = async (id) => {
    if (revokingId) return;
    setRevokingId(id);
    const token = localStorage.getItem('token');
    try {
      await revokePasskey(token, id);
      toast.success('Passkey removed.');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove passkey.');
    } finally {
      setRevokingId(null);
    }
  };

  // Browser doesn't support WebAuthn at all → hide the card silently.
  if (supported === false || !browserSupportsPasskeys()) {
    return null;
  }
  // Initial capability check still in flight → render nothing yet (avoids flash).
  if (supported === null || loading) {
    return null;
  }

  const hasCredentials = credentials.length > 0;

  return (
    <div
      data-testid="biometric-unlock-card"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        borderRadius: 18,
        padding: '18px 18px 14px',
        boxShadow: 'var(--premium-shadow-main)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 12,
            background: 'rgba(112, 63, 246, 0.10)',
            color: BRAND,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Fingerprint size={20} strokeWidth={2.2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, fontFamily: FONT, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>
            Biometric unlock
          </h3>
          <p style={{ margin: '4px 0 0', fontFamily: SUB_FONT, fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.4 }}>
            Use your fingerprint, Face ID or device PIN to unlock Allure when you reopen the app.
          </p>
        </div>
      </div>

      {!hasCredentials && (
        <button
          onClick={handleEnrol}
          disabled={enrolling}
          data-testid="biometric-unlock-enrol-button"
          style={{
            width: '100%',
            padding: '13px 16px',
            borderRadius: 14,
            border: 'none',
            background: enrolling
              ? 'rgba(112, 63, 246, 0.4)'
              : 'linear-gradient(135deg, #703FF6 0%, #4A20C9 100%)',
            color: '#FFFFFF',
            fontFamily: SUB_FONT,
            fontSize: 14,
            fontWeight: 700,
            cursor: enrolling ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <ShieldCheck size={16} strokeWidth={2.2} />
          {enrolling ? 'Setting up…' : 'Enable biometric unlock on this device'}
        </button>
      )}

      {hasCredentials && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {credentials.map((cred) => (
              <div
                key={cred.id}
                data-testid={`biometric-unlock-credential-${cred.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  background: 'rgba(112, 63, 246, 0.06)',
                  border: '1px solid rgba(112, 63, 246, 0.10)',
                  borderRadius: 12,
                }}
              >
                <Smartphone size={18} color={BRAND} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontFamily: SUB_FONT, fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>
                    {_shortDeviceLabel(cred)}
                  </p>
                  <p style={{ margin: '2px 0 0', fontFamily: SUB_FONT, fontSize: 11.5, color: 'var(--text-2)' }}>
                    Added {_formatDate(cred.created_at)}
                    {cred.last_used_at ? ` · Used ${_formatDate(cred.last_used_at)}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => handleRevoke(cred.id)}
                  disabled={revokingId === cred.id}
                  data-testid={`biometric-unlock-revoke-${cred.id}`}
                  aria-label="Remove passkey"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 999,
                    border: '1px solid rgba(229, 57, 53, 0.18)',
                    background: 'rgba(229, 57, 53, 0.06)',
                    color: '#C93636',
                    cursor: revokingId === cred.id ? 'wait' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Trash2 size={14} strokeWidth={2.2} />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={handleEnrol}
            disabled={enrolling}
            data-testid="biometric-unlock-add-button"
            style={{
              alignSelf: 'flex-start',
              padding: '8px 14px',
              borderRadius: 999,
              border: `1.5px dashed ${BRAND}`,
              background: 'transparent',
              color: BRAND,
              fontFamily: SUB_FONT,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: enrolling ? 'wait' : 'pointer',
            }}
          >
            {enrolling ? 'Setting up…' : '+ Add another device'}
          </button>
        </>
      )}
    </div>
  );
};

export default BiometricUnlockCard;
